import { CheckCircle } from '@mui/icons-material';
import { Avatar, CircularProgress } from '@mui/material';
import { NetworkIcon } from 'elements/NetworkIcon';
import { NETWORKS } from 'lib/constants';
import { useApi } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

interface PartialUser {
  /** If the user is on Gondola already it has an id, name, and avatar */
  id?: number;
  name?: string;
  avatarUrl?: string;
  /** Every user -- whether from Gondola or outside platform -- has a username */
  username: string;
  /** Users from outside platforms have a network */
  network?: number;
}

interface Props {
  onClick?: (user: PartialUser) => void;
  user: PartialUser;
  selected?: boolean;
  /**
  * The index of the user in the users array.
  * Useful for passing back the full selected user
  * to replace what was there before even if the user
  * we were passed didn't have an id.
  **/
  index: number;
}

export const CreditPillButton = ({
  user, selected, onClick, index,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [_user, setUser] = useState<PartialUser>(user);
  const [isLoading, setIsLoading] = useState(false);
  const color = selected ? 'bg-mediumgray text-white' : '';
  const checkColor = selected ? 'secondary' : 'info';
  const label = user?.name || user.username;

  const handleClick = async () => {
    if (!_user.id && _user.network && _user.username) {
      try {
        setIsLoading(true);
        const result = await API.findByNetwork(_user.network as number, _user.username);
        const newUser = { ...result, index, id: result.userId };
        setUser(newUser);
        if (onClick) onClick(newUser);
      } catch (err) {
        enqueueSnackbar(`Error finding user @${_user.username} via ${NETWORKS[_user.network].title}. You can try again in a moment.`, { variant: 'error' });
        console.error(err);
      }
      setIsLoading(false);
    } else if (onClick) onClick(_user);
  };

  /**
   * Render the checkmark if the pill button is selected, the user's avatar, the network icon,
   * or nothing
   **/
  const renderAvatar = () => {
    if (selected) {
      return <CheckCircle color={checkColor} className="mr-2" />;
    }
    if (_user.avatarUrl && !selected) {
      return (
        <Avatar
          src={_user.avatarUrl}
          alt={label}
          className="mr-2"
          sx={{ width: 24, height: 24 }}
        />
      );
    }
    if (_user.network) {
      return (<span className="mr-2"><NetworkIcon network={_user.network} /></span>);
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="p-4 border-dashed">
        <CircularProgress size="2rem" />
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={`pill-${label}`}
      className={`flex items-center border border-mediumgray font-semibold px-2 py-1 rounded-xl hover:bg-lightgray ${color}`}
      onClick={handleClick}
      disabled={!onClick}
    >
      {renderAvatar()}
      {label}
    </button>
  );
};
